import { z } from 'zod';
import { SubscriptionPlanIdSchema } from './billing.js';

// ─────────────────────────────────────────────────────────────────
// Plan entitlements — the SINGLE source of what each plan allows.
//
// Content is workspace-scoped; the entitlement of a workspace comes
// from its live subscription (active/trialing → that plan, otherwise
// `free`). Both the backend guards (billing/entitlement.ts) and the
// editor UI (useEntitlement) read these constants/schemas so a limit
// can never drift between enforcement and presentation.
//
// Free: 3 blog posts total (any status), no emails, no components.
// Paid tiers: unlimited posts + full feature access.
// ─────────────────────────────────────────────────────────────────

export const PlanFeatureSchema = z.object({
  emails: z.boolean(),
  components: z.boolean(),
});
export type PlanFeatures = z.infer<typeof PlanFeatureSchema>;

export const PlanLimitsSchema = z.object({
  /** Max posts the workspace may hold (null = unlimited). */
  maxPosts: z.number().int().nullable(),
  maxEmails: z.number().int().nullable(),
  /** Max email senders the workspace may configure (null = unlimited). Free allows 1. */
  maxSenders: z.number().int().nullable(),
  /** Max audience lists the workspace may hold (null = unlimited). Free allows 1. */
  maxEmailLists: z.number().int().nullable(),
  /** Max subscribers in ONE list (null = unlimited). Free allows 500. */
  maxSubscribersPerList: z.number().int().nullable(),
});
export type PlanLimits = z.infer<typeof PlanLimitsSchema>;

export interface PlanEntitlement {
  plan: z.infer<typeof SubscriptionPlanIdSchema>;
  features: PlanFeatures;
  limits: PlanLimits;
}

/** Entitlement per plan id. `free` is the fallback for no live subscription. */
export const PLAN_LIMITS: Record<z.infer<typeof SubscriptionPlanIdSchema>, PlanEntitlement> = {
  free: {
    plan: 'free',
    features: { emails: true, components: false },
    limits: {
      maxPosts: 3,
      maxSenders: 1,
      maxEmails: 3,
      maxEmailLists: 1,
      maxSubscribersPerList: 500,
    },
  },
  individual: {
    plan: 'individual',
    features: { emails: true, components: true },
    limits: {
      maxPosts: null,
      maxSenders: null,
      maxEmails: null,
      maxEmailLists: null,
      maxSubscribersPerList: null,
    },
  },
  pro: {
    plan: 'pro',
    features: { emails: true, components: true },
    limits: {
      maxPosts: null,
      maxSenders: null,
      maxEmails: null,
      maxEmailLists: null,
      maxSubscribersPerList: null,
    },
  },
  enterprise: {
    plan: 'enterprise',
    features: { emails: true, components: true },
    limits: {
      maxPosts: null,
      maxSenders: null,
      maxEmails: null,
      maxEmailLists: null,
      maxSubscribersPerList: null,
    },
  },
};

export const entitlementForPlan = (
  plan: z.infer<typeof SubscriptionPlanIdSchema>,
): PlanEntitlement => PLAN_LIMITS[plan] ?? PLAN_LIMITS.individual;

/** Full entitlement incl. live usage — what GET /api/billing/entitlement returns. */
export const EntitlementSchema = z.object({
  plan: SubscriptionPlanIdSchema,
  features: PlanFeatureSchema,
  limits: PlanLimitsSchema,
  usage: z.object({
    posts: z.number().int(),
    emails: z.number().int(),
    senders: z.number().int(),
    /** Audience lists the workspace holds. */
    emailLists: z.number().int(),
    /** Subscribers across ALL of the workspace's lists. */
    subscribers: z.number().int(),
  }),
});
export type Entitlement = z.infer<typeof EntitlementSchema>;

/** Codes the UI recognises when a create request is refused. */
export const EntitlementErrorCodeSchema = z.enum(['feature_required', 'limit_reached']);
export type EntitlementErrorCode = z.infer<typeof EntitlementErrorCodeSchema>;

/** Payload shape of 403s thrown by the entitlement guards. */
export const EntitlementErrorPayloadSchema = z.object({
  error: z.string(),
  code: EntitlementErrorCodeSchema,
  plan: SubscriptionPlanIdSchema,
  feature: PlanFeatureSchema.keyof().optional(),
  limit: z.number().int().nullable().optional(),
  current: z.number().int().optional(),
});
export type EntitlementErrorPayload = z.infer<typeof EntitlementErrorPayloadSchema>;
